import { useState } from 'react';
import { Button, TextInput } from '@c2fo/liquidity';
import { EyeOpenIcon, EyeCrossedIcon } from '@c2fo/liquidity/icons';
import {
  createCredential,
  deleteCredential,
  uploadCredentialFile,
  getFieldConfigsForCredentialType,
  validateCredentialValues,
} from '../../api/Credentials';
import { CredentialType } from '../../api/shared';
import { useApiContext } from '../../contexts/FileDeliveryContext';
import type { Credential } from '../../api/Credentials/types';
import { Modal } from '../Modal';
import { FormField } from '../FormField';
import { CredentialFileField } from '../CredentialFileField';
import { FieldHelpTooltip } from '../FieldHelpTooltip';
import { AdvancedCredentialEditor } from '../AdvancedCredentialEditor';

interface NewCredentialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (credential: Credential) => void;
  onError: (message: string) => void;
}

const CREDENTIAL_TYPES = [
  { value: CredentialType.CREDENTIAL_TYPE_SSH, label: 'SFTP', description: 'SSH key credential' },
  { value: CredentialType.CREDENTIAL_TYPE_AWS, label: 'AWS', description: 'AWS S3 access credentials' },
  {
    value: CredentialType.CREDENTIAL_TYPE_GCP_SERVICE_ACCOUNT,
    label: 'GCP',
    description: 'Google Cloud service account',
  },
  {
    value: CredentialType.CREDENTIAL_TYPE_AZURE_SERVICE_PRINCIPAL,
    label: 'Azure',
    description: 'Azure service principal',
  },
];

interface PendingFileUpload {
  fieldKey: string;
  filename: string;
  content: string;
}

export const NewCredentialModal: React.FC<NewCredentialModalProps> = ({ isOpen, onClose, onCreated, onError }) => {
  const apiContext = useApiContext();
  const [credentialType, setCredentialType] = useState<CredentialType>(CredentialType.CREDENTIAL_TYPE_SSH);
  const [displayName, setDisplayName] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [pendingFileUploads, setPendingFileUploads] = useState<PendingFileUpload[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [editorMode, setEditorMode] = useState<'simple' | 'advanced'>('simple');

  const fieldConfigs = getFieldConfigsForCredentialType(credentialType);

  const isFormValid = () => {
    if (!displayName.trim()) return false;
    const validation = validateCredentialValues(credentialType, values);
    return validation.isValid;
  };

  const handleTypeChange = (type: CredentialType) => {
    setCredentialType(type);
    setValues({});
    setShowSecrets({});
    setPendingFileUploads([]);
    setEditorMode('simple');
  };

  const handleValueChange = (key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const toggleShowSecret = (key: string) => {
    setShowSecrets((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleFileContent = (fieldKey: string, content: string, filename: string) => {
    setPendingFileUploads((prev) => {
      const filtered = prev.filter((f) => f.fieldKey !== fieldKey);
      return [...filtered, { fieldKey, filename, content }];
    });
  };

  const handleSave = async () => {
    if (!displayName.trim()) {
      onError('Display name is required');
      return;
    }

    const validation = validateCredentialValues(credentialType, values);
    if (!validation.isValid) {
      onError(validation.errors[0]?.message || 'Validation failed');
      return;
    }

    setIsSaving(true);
    let createdCredential: Credential | null = null;

    try {
      createdCredential = await createCredential(apiContext, {
        credential: {
          name: '',
          displayName: displayName.trim(),
          credentialType,
          values,
        },
      });

      if (pendingFileUploads.length > 0) {
        for (const fileUpload of pendingFileUploads) {
          await uploadCredentialFile(apiContext, {
            name: createdCredential.name,
            filename: values[fileUpload.fieldKey] || fileUpload.filename,
            content: fileUpload.content,
          });
        }
      }

      onCreated(createdCredential);
      handleClose();
    } catch (error) {
      if (createdCredential) {
        try {
          await deleteCredential(apiContext, createdCredential.name);
        } catch {
          // Ignore delete errors during rollback
        }
      }

      onError('Failed to create credential credential. File upload may have failed.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setCredentialType(CredentialType.CREDENTIAL_TYPE_SSH);
    setDisplayName('');
    setValues({});
    setShowSecrets({});
    setPendingFileUploads([]);
    setEditorMode('simple');
    onClose();
  };

  const handleAdvancedValuesChange = (newValues: Record<string, string>) => {
    setValues(newValues);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="New Credential"
      footer={
        <>
          <Button variant="tertiary" className="rounded-md" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            className="rounded-md text-white"
            onClick={handleSave}
            disabled={isSaving || !isFormValid()}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </>
      }
    >
      <FormField label="Display Name" required>
        <TextInput
          value={displayName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDisplayName(e.target.value)}
          placeholder="Enter a name for this credential"
          className="w-full"
          size="sm"
        />
      </FormField>

      <fieldset>
        <legend className="block text-sm font-medium text-gray-700 mb-3">
          Credential Type <span className="text-red-500">*</span>
        </legend>
        <div className="grid grid-cols-2 gap-3">
          {CREDENTIAL_TYPES.map((type) => (
            <div
              key={type.value}
              className={`flex items-start gap-3 p-3 border rounded-lg transition-colors ${
                credentialType === type.value
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                id={`credential-type-${type.value}`}
                type="radio"
                name="credentialType"
                value={type.value}
                checked={credentialType === type.value}
                onChange={() => handleTypeChange(type.value)}
                className="mt-1"
              />
              <label htmlFor={`credential-type-${type.value}`} className="cursor-pointer">
                <div className="font-medium text-sm text-gray-900">{type.label}</div>
                <div className="text-xs text-gray-500">{type.description}</div>
              </label>
            </div>
          ))}
        </div>
      </fieldset>

      {fieldConfigs.length > 0 && (
        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-900">Credential Values</h3>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setEditorMode('simple')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  editorMode === 'simple'
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                Simple
              </button>
              <button
                type="button"
                onClick={() => setEditorMode('advanced')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  editorMode === 'advanced'
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                Advanced (JSON)
              </button>
            </div>
          </div>

          {editorMode === 'advanced' ? (
            <AdvancedCredentialEditor
              credentialType={credentialType}
              values={values}
              onChange={handleAdvancedValuesChange}
            />
          ) : (
            <div className="space-y-4">
              {fieldConfigs.map((field) => {
                if (field.type === 'filePath') {
                  return (
                    <CredentialFileField
                      key={field.key}
                      label={field.label}
                      pathValue={values[field.key] || ''}
                      onPathChange={(path) => handleValueChange(field.key, path)}
                      onFileContent={(content, filename) => handleFileContent(field.key, content, filename)}
                      helpText={field.helpText}
                      helpSeverity={field.helpSeverity}
                      required={field.required}
                      placeholder={field.placeholder}
                    />
                  );
                }

                if (field.type === 'secret') {
                  return (
                    <div key={field.key}>
                      <div className="flex items-center gap-1 mb-1">
                        <label className="block text-sm font-medium text-gray-700">
                          {field.label}
                          {field.required && <span className="text-red-500 ml-0.5">*</span>}
                        </label>
                        <FieldHelpTooltip helpText={field.helpText} severity={field.helpSeverity} />
                      </div>
                      <div className="relative">
                        <TextInput
                          type={showSecrets[field.key] ? 'text' : 'password'}
                          value={values[field.key] || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleValueChange(field.key, e.target.value)
                          }
                          placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                          className="w-full pr-10"
                          size="sm"
                        />
                        <button
                          type="button"
                          onClick={() => toggleShowSecret(field.key)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                        >
                          {showSecrets[field.key] ? (
                            <EyeOpenIcon className="h-4 w-4" />
                          ) : (
                            <EyeCrossedIcon className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={field.key}>
                    <div className="flex items-center gap-1 mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-0.5">*</span>}
                      </label>
                      <FieldHelpTooltip helpText={field.helpText} severity={field.helpSeverity} />
                    </div>
                    <TextInput
                      value={values[field.key] || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleValueChange(field.key, e.target.value)
                      }
                      placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                      className="w-full"
                      size="sm"
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};
